import React from 'react';
import { Bot, User } from 'lucide-react';

/**
 * Selo "quem responde" (2026-10-19, validação do widget como 5º inbox).
 *
 * UMA regra para lista, cabeçalho e ficha, nos 5 canais: quem responde é o
 * responsável. Agente atribuído e agente em modo automático (principal
 * respondendo sem atribuição gravada) têm a MESMA cor e o MESMO texto; a
 * única diferença é o sufixo discreto "· automático" quando não há
 * atribuição gravada. Membro responsável usa o mesmo selo, com o nome.
 * Antes: a lista pintava o agente atribuído de laranja e o automático com a
 * cor de destaque (verde), e o cliente via dois selos para a mesma ideia.
 *
 * - `assigned`: assigned_to do contato ({type: member|agent|none, name}).
 *   `none` = transferido para ninguém: nada é mostrado.
 * - `autoAgentName`: nome do agente que responde sem atribuição (só quando
 *   o shell já decidiu que o agente está ativo para este contato).
 * - `variant`: 'chip' (lista, 10px), 'text' (linha do cabeçalho, 11px) ou
 *   'pill' (ficha do contato, 12px).
 */
export const ASSIGNEE_BG = 'rgba(245,158,11,0.14)';
export const ASSIGNEE_FG = '#d97706';
export const ASSIGNEE_DOT = '#f59e0b';

const SUFIXO_AUTOMATICO = 'automático';

export default function WhoAnswersBadge({ assigned, autoAgentName = null, variant = 'chip', title }) {
  const gravado = assigned && assigned.type && assigned.type !== 'none' ? assigned : null;
  if (!gravado && !autoAgentName) return null;

  const isAgent = gravado ? gravado.type === 'agent' : true;
  const nome = gravado
    ? (gravado.name || (isAgent ? 'Agente IA' : 'Membro da equipe'))
    : (autoAgentName || 'Agente IA');
  const automatico = !gravado;
  const tooltip = title || (automatico
    ? `Atendimento automático: ${nome} responde este contato até ser transferido`
    : `Responsável: ${nome}`);
  const iconSize = variant === 'pill' ? 12 : 10;
  const Icon = isAgent ? Bot : User;

  const sufixo = automatico ? (
    <span style={{ fontWeight: 400, opacity: 0.8, whiteSpace: 'nowrap' }}>· {SUFIXO_AUTOMATICO}</span>
  ) : null;

  if (variant === 'text') {
    return (
      <span style={{ fontSize: '11px', color: ASSIGNEE_FG, display: 'flex', alignItems: 'center', gap: '3px', minWidth: 0 }} title={tooltip}>
        <Icon size={iconSize} style={{ flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nome}</span>
        {sufixo}
      </span>
    );
  }

  if (variant === 'pill') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
        backgroundColor: ASSIGNEE_BG, color: ASSIGNEE_FG,
        fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
      }} title={tooltip}>
        <Icon size={iconSize} />
        {nome}
        {sufixo}
      </span>
    );
  }

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      backgroundColor: ASSIGNEE_BG, color: ASSIGNEE_FG,
      fontSize: 10, fontWeight: 600, padding: '1px 6px',
      borderRadius: 4, flexShrink: 0, lineHeight: '16px', maxWidth: 180,
    }} title={tooltip}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: ASSIGNEE_DOT, flexShrink: 0 }} />
      {isAgent && <Icon size={iconSize} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nome}</span>
      {sufixo}
    </span>
  );
}
